/**
 * PropertyCreateWizardPage — route `/properties/new` (PMS portal).
 *
 * SCRUM-178 (P3.1) — Create wizard chrome + wired steps.
 * SCRUM-179 (P3.2) — Create wizard shell steps 3 + 4.
 *
 * Replaces the SCRUM-168 P0 NotConnectedYet placeholder with the
 * bundle's 6-step wizard per
 * `~/Desktop/design_handoff_properties/CREATE_WIZARD_SPEC.md`:
 *
 *   1. Basics — name + type RadioCard (wired)
 *   2. Address — street/city/state/zip (wired)
 *   3. Spaces — inline editor (SHELL — BE has no Space entity)
 *   4. Units composition — Whole property vs. Per-space (SHELL)
 *   5. Content — AI generate stub + textarea (chrome wired;
 *      AI stub renders the bundle's boilerplate copy)
 *   6. Confirm — DetailRow summary + POST /properties on submit
 *
 * Modal-style centered container, stepper at top, form content,
 * Back / Cancel / Continue / Create property footer. Cancel from any
 * step opens a discard-confirm modal.
 *
 * Gated by `property.write` at the route level. The POST consumes
 * `useCreateProperty()` and navigates to `/properties/:id` on
 * success.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Icon,
  TextField,
} from '@sparta/ui-kit';
import { RadioCard } from '../_components/RadioCard.js';
import { DetailRow } from '../_components/DetailRow.js';
import { NotConnectedYet } from '../../../entities/not-connected-yet/index.js';
import { useCreateProperty } from '../api/use-create-property.js';
import { useSrAnnouncement } from '../../../shared/hooks/useSrAnnouncement.js';
import { useFocusTrap } from '../../../shared/hooks/useFocusTrap.js';

type Step = 1 | 2 | 3 | 4 | 5 | 6;
const STEPS: readonly { id: Step; label: string }[] = [
  { id: 1, label: 'Basics' },
  { id: 2, label: 'Address' },
  { id: 3, label: 'Spaces' },
  { id: 4, label: 'Units' },
  { id: 5, label: 'Content' },
  { id: 6, label: 'Confirm' },
];

const TYPE_OPTIONS = [
  {
    value: 'villa' as const,
    title: 'Villa',
    sub: 'Detached luxury home',
  },
  {
    value: 'home' as const,
    title: 'Home',
    sub: 'Standalone house',
  },
  {
    value: 'condominium' as const,
    title: 'Condominium',
    sub: 'Multi-tenant building',
  },
  {
    value: 'cabin' as const,
    title: 'Cabin',
    sub: 'Rural escape',
  },
];

type CategoryValue = (typeof TYPE_OPTIONS)[number]['value'];

interface FormState {
  name: string;
  category: CategoryValue;
  street: string;
  city: string;
  state: string;
  zip: string;
  composition: 'whole' | 'per-space';
  description: string;
}

const INITIAL_FORM: FormState = {
  name: '',
  category: 'villa',
  street: '',
  city: '',
  state: '',
  zip: '',
  composition: 'whole',
  description: '',
};

export function PropertyCreateWizardPage(): React.ReactElement {
  const navigate = useNavigate();
  const [step, setStep] = React.useState<Step>(1);
  const [form, setForm] = React.useState<FormState>(INITIAL_FORM);
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  const [aiGenerated, setAiGenerated] = React.useState(false);
  const [aiPending, setAiPending] = React.useState(false);
  const { mutateAsync, status, errorMessage } = useCreateProperty();

  // Per ACCESSIBILITY.md §6: announce step advance + AI generation
  // start / success. Step advance uses polite. AI success / start
  // also polite (no visible live region duplicating it).
  //
  // Submit errors are NOT announced by this hook — the visible
  // `role="alert"` banner rendered below the footer already
  // announces the failure assertively, and Codex round-3 flagged
  // the duplicate `sr.assertive(...)` path here as a doubled SR
  // experience (the UnitStatusModal followed the same single-source
  // pattern). We keep the inline alert as the single announcement
  // surface for submit failures.
  const sr = useSrAnnouncement();
  const previousStep = React.useRef<Step>(step);
  React.useEffect(() => {
    if (previousStep.current !== step) {
      const label = STEPS.find((s) => s.id === step)?.label ?? '';
      sr.polite(`Step ${step} of 6: ${label}`);
      previousStep.current = step;
    }
  }, [step, sr]);

  const updateForm = (patch: Partial<FormState>): void => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const validateStep = (s: Step): boolean => {
    if (s === 1) return form.name.trim().length > 0;
    if (s === 2)
      return (
        form.street.trim().length > 0 &&
        form.city.trim().length > 0 &&
        form.state.trim().length > 0 &&
        form.zip.trim().length > 0
      );
    return true;
  };

  const stepValid = validateStep(step);

  const handleContinue = (): void => {
    if (!stepValid) return;
    if (step < 6) setStep((step + 1) as Step);
  };

  const handleBack = (): void => {
    if (step > 1) setStep((step - 1) as Step);
  };

  const handleCancel = (): void => {
    // Only show discard confirm if the form is dirty.
    const dirty =
      form.name !== '' ||
      form.street !== '' ||
      form.city !== '' ||
      form.description !== '';
    if (dirty) setConfirmDiscard(true);
    else navigate('/properties');
  };

  const handleAi = async (): Promise<void> => {
    setAiPending(true);
    sr.polite('Generating description…');
    // The bundle's spec calls for a 600ms simulated delay + a
    // boilerplate description seeded from name + type + city. No
    // backend endpoint exists today (SCRUM-176 / SCRUM-186 follow-
    // ups); the honest UX is "not connected yet" once the user
    // clicks. We simulate the delay so the perf cost lands in the
    // design's expected place.
    await new Promise((r) => setTimeout(r, 600));
    setAiPending(false);
    const seed = `A ${form.category} in ${form.city || '—'}. ${form.name} sleeps comfortably for guests, with all the essentials. Edit this draft before publishing.`;
    updateForm({ description: seed });
    setAiGenerated(true);
    sr.polite('Description generated');
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      const newId = await mutateAsync({
        unitName: form.name,
        unitCategory: form.category,
        address: {
          line1: form.street,
          city: form.city,
          stateOrProvince: form.state,
          postalCode: form.zip,
          // Defaults — the bundle's wizard doesn't collect country /
          // timezone explicitly. v1 ships US / America/Los_Angeles;
          // future tickets surface a region picker.
          countryCode: 'US',
          timezone: 'America/Los_Angeles',
        },
        capacity: {
          bedrooms: 1,
          maxAdults: 2,
          maxSleeps: 2,
          // Required by the contract (1 ≤ standardCapacity ≤ maxSleeps): guests
          // included before extra-guest fees apply. The v1 wizard does not yet
          // collect this, so default to maxSleeps (all guests are "standard").
          standardCapacity: 2,
          bathrooms: { full: 1, half: 0, showerOnly: 0, bathtubOnly: 0 },
          allowsChildren: true,
          allowsPets: false,
        },
        status: { billing: 'active', visibility: 'non_bookable' },
        ...(form.description.length > 0 ? { description: form.description } : {}),
      });
      navigate(`/properties/${newId}`);
    } catch {
      // errorMessage is surfaced inline below the footer; the wizard
      // stays on step 6 so the user can retry.
    }
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-8 sm:py-10">
      {sr.regions}
      <header className="mb-4 flex items-center justify-between gap-3">
        <Badge variant="brand" size="sm">
          New property
        </Badge>
        <Button variant="ghost" size="sm" onClick={handleCancel}>
          <Icon name="close" size={14} aria-hidden />
          <span className="ml-2 sr-only">Cancel</span>
        </Button>
      </header>

      <h1
        className="text-2xl font-semibold m-0"
        style={{ color: 'var(--fg-primary)', letterSpacing: '-0.025em' }}
      >
        Add a property
      </h1>
      <p
        className="mt-1.5 mb-4 text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        Once basics are in, we’ll seed the description with AI.
        Override before publishing.
      </p>

      {/* Stepper — per CREATE_WIZARD_SPEC.md:
            Desktop: horizontal segments with check/number circles
            Mobile:  compact card "Step N of 6 · {step name}" + progress bar
          Both versions live in the DOM; CSS visibility switches at
          the `sm:` breakpoint (640px Tailwind default). The desktop
          `<ol>` keeps its `aria-label="Wizard progress"`; the mobile
          card surfaces the same content as visible text, so screen
          readers always have one source of truth regardless of
          viewport. */}
      <div className="mb-4">
        {/* Mobile-compressed (below sm). Hidden on desktop. */}
        <div className="sm:hidden">
          {(() => {
            const activeStep = STEPS.find((s) => s.id === step);
            const progressPct = (step / 6) * 100;
            return (
              <div
                className="rounded-xl p-3"
                style={{
                  background: 'var(--bg-sunken)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                <p
                  className="m-0 text-xs font-semibold"
                  style={{ color: 'var(--fg-primary)' }}
                >
                  Step {step} of 6 · {activeStep?.label ?? ''}
                </p>
                <div
                  className="mt-2 rounded-full overflow-hidden h-1.5"
                  style={{ background: 'var(--border-subtle)' }}
                >
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${progressPct}%`,
                      background: 'var(--brand-500)',
                    }}
                  />
                </div>
              </div>
            );
          })()}
        </div>
        {/* Desktop horizontal stepper (sm and up). Hidden on mobile. */}
        <ol
          aria-label="Wizard progress"
          className="hidden sm:flex flex-wrap items-center gap-2 text-xs font-semibold"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          {STEPS.map((s) => {
            const isActive = s.id === step;
            const isComplete = s.id < step;
            return (
              <li
                key={s.id}
                className="inline-flex items-center gap-1.5"
                aria-current={isActive ? 'step' : undefined}
              >
                <span
                  className="inline-flex items-center justify-center rounded-full w-5 h-5 text-xs"
                  style={{
                    background: isActive
                      ? 'var(--brand-500)'
                      : isComplete
                        ? 'var(--success-500)'
                        : 'var(--bg-sunken)',
                    color: isActive || isComplete ? '#fff' : 'var(--fg-tertiary)',
                  }}
                >
                  {isComplete ? <Icon name="check" size={12} aria-hidden /> : s.id}
                </span>
                <span
                  style={{
                    color: isActive ? 'var(--fg-primary)' : undefined,
                  }}
                >
                  {s.label}
                </span>
                {s.id < 6 ? <span aria-hidden>·</span> : null}
              </li>
            );
          })}
        </ol>
      </div>

      <Card padding="lg">
        {step === 1 ? (
          <Step1Basics form={form} onChange={updateForm} />
        ) : null}
        {step === 2 ? (
          <Step2Address form={form} onChange={updateForm} />
        ) : null}
        {step === 3 ? <Step3SpacesShell /> : null}
        {step === 4 ? (
          <Step4UnitsShell form={form} onChange={updateForm} />
        ) : null}
        {step === 5 ? (
          <Step5Content
            form={form}
            onChange={updateForm}
            aiGenerated={aiGenerated}
            aiPending={aiPending}
            onGenerate={() => {
              void handleAi();
            }}
          />
        ) : null}
        {step === 6 ? <Step6Confirm form={form} /> : null}
      </Card>

      {errorMessage !== null ? (
        <div
          role="alert"
          className="mt-3 rounded-lg p-3 text-sm"
          style={{
            background:
              'color-mix(in oklab, var(--danger-500) 12%, var(--bg-surface))',
            color: 'var(--fg-danger-on-tint)',
            border:
              '1px solid color-mix(in oklab, var(--danger-500) 30%, transparent)',
          }}
        >
          Couldn’t create the property. {errorMessage}
        </div>
      ) : null}

      <footer className="mt-4 flex items-center justify-between gap-3">
        <div className="flex gap-2">
          {step > 1 ? (
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <Icon name="chevLeft" size={14} aria-hidden />
              <span className="ml-2">Back</span>
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
        <span
          className="hidden sm:inline text-xs"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          Step {step} of 6
        </span>
        {step < 6 ? (
          <Button
            variant="primary"
            size="sm"
            disabled={!stepValid}
            onClick={handleContinue}
          >
            <span className="mr-2">Continue</span>
            <Icon name="arrowRight" size={14} aria-hidden />
          </Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            disabled={status === 'pending'}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} aria-hidden />
            <span className="ml-2">
              {status === 'pending' ? 'Creating…' : 'Create property'}
            </span>
          </Button>
        )}
      </footer>

      {confirmDiscard ? (
        <DiscardConfirm
          onKeep={() => setConfirmDiscard(false)}
          onDiscard={() => navigate('/properties')}
        />
      ) : null}
    </div>
  );
}

interface StepProps {
  readonly form: FormState;
  readonly onChange: (patch: Partial<FormState>) => void;
}

function Step1Basics({
  form,
  onChange,
}: StepProps): React.ReactElement {
  return (
    <div>
      <h2
        className="m-0 mb-1 text-base font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Basics
      </h2>
      <p
        className="m-0 mb-4 text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        Name and category.
      </p>
      <TextField
        label="Property name"
        placeholder="Pacific Heights Townhome"
        value={form.name}
        onChange={(e) => onChange({ name: e.currentTarget.value })}
      />
      <p
        className="text-xs font-bold uppercase mt-3 mb-2"
        style={{
          color: 'var(--fg-tertiary)',
          letterSpacing: '0.07em',
        }}
      >
        Type
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {TYPE_OPTIONS.map((opt) => (
          <RadioCard
            key={opt.value}
            name="category"
            value={opt.value}
            checked={form.category === opt.value}
            title={opt.title}
            sub={opt.sub}
            onChange={(v) => onChange({ category: v as CategoryValue })}
          />
        ))}
      </div>
    </div>
  );
}

function Step2Address({
  form,
  onChange,
}: StepProps): React.ReactElement {
  return (
    <div>
      <h2
        className="m-0 mb-1 text-base font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Address
      </h2>
      <p
        className="m-0 mb-4 text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        Used for STR licensing and OTA listings.
      </p>
      <TextField
        label="Street"
        value={form.street}
        onChange={(e) => onChange({ street: e.currentTarget.value })}
      />
      <div className="grid gap-2 sm:grid-cols-3 mt-3">
        <TextField
          label="City"
          value={form.city}
          onChange={(e) => onChange({ city: e.currentTarget.value })}
        />
        <TextField
          label="State"
          value={form.state}
          onChange={(e) => onChange({ state: e.currentTarget.value })}
        />
        <TextField
          label="Zip"
          value={form.zip}
          onChange={(e) => onChange({ zip: e.currentTarget.value })}
        />
      </div>
    </div>
  );
}

function Step3SpacesShell(): React.ReactElement {
  return (
    <div>
      <h2
        className="m-0 mb-1 text-base font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Spaces
      </h2>
      <p
        className="m-0 mb-4 text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        Define the physical pieces. Units come next.
      </p>
      <NotConnectedYet
        surface="Wizard Step 3 — Spaces"
        ticket="SCRUM-188"
      />
    </div>
  );
}

function Step4UnitsShell({
  form,
  onChange,
}: StepProps): React.ReactElement {
  return (
    <div>
      <h2
        className="m-0 mb-1 text-base font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Units
      </h2>
      <p
        className="m-0 mb-4 text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        Pick how guests can book. Single-unit is fine.
      </p>
      <div className="grid gap-2 sm:grid-cols-2 mb-4">
        <RadioCard
          name="composition"
          value="whole"
          checked={form.composition === 'whole'}
          title="Whole property"
          sub="Sells everything at once. Simple, no SIM conflicts."
          onChange={(v) => onChange({ composition: v as 'whole' | 'per-space' })}
        />
        <RadioCard
          name="composition"
          value="per-space"
          checked={form.composition === 'per-space'}
          title="Per-space (Split Inventory)"
          sub="Each space rentable independently. We’ll auto-resolve conflicts."
          onChange={(v) => onChange({ composition: v as 'whole' | 'per-space' })}
        />
      </div>
      {/* SCRUM-179 built the FE radio cards; the composition choice
          persists in form state but doesn't drive the POST today (BE
          DTO has no splitInventory flag — that lands with SCRUM-188
          Spaces+Units BE entity). The shell-vs-wired gap is named
          here to keep contributors honest. */}
      <NotConnectedYet
        surface="Composition is captured but not sent to the BE yet"
        ticket="SCRUM-188"
      />
    </div>
  );
}

interface Step5Props extends StepProps {
  readonly aiGenerated: boolean;
  readonly aiPending: boolean;
  readonly onGenerate: () => void;
}

function Step5Content({
  form,
  onChange,
  aiGenerated,
  aiPending,
  onGenerate,
}: Step5Props): React.ReactElement {
  return (
    <div>
      <h2
        className="m-0 mb-1 text-base font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Content
      </h2>
      <p
        className="m-0 mb-4 text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        Seed the description with AI, photos later.
      </p>
      {!aiGenerated ? (
        <div
          className="rounded-xl p-4 flex items-start gap-3 mb-3"
          style={{
            background:
              'color-mix(in oklab, var(--brand-500) 8%, var(--bg-surface))',
            border:
              '1px solid color-mix(in oklab, var(--brand-500) 20%, transparent)',
          }}
        >
          <Icon
            name="sparkles"
            size={20}
            aria-hidden
            style={{ color: 'var(--brand-500)' }}
          />
          <div className="flex-1">
            <p
              className="m-0 text-sm font-semibold"
              style={{ color: 'var(--fg-primary)' }}
            >
              Generate a starter description
            </p>
            <p
              className="m-0 mt-1 mb-3 text-xs"
              style={{ color: 'var(--fg-secondary)' }}
            >
              Based on your name, type, and address. Editable after
              generation.
            </p>
            <Button
              variant="primary"
              size="sm"
              disabled={aiPending}
              onClick={onGenerate}
            >
              <Icon name="sparkles" size={14} aria-hidden />
              <span className="ml-2">
                {aiPending ? 'Generating…' : 'Generate with AI'}
              </span>
            </Button>
          </div>
        </div>
      ) : null}
      <label className="block">
        <span
          className="block text-xs font-semibold mb-1.5"
          style={{ color: 'var(--fg-primary)' }}
        >
          Description
        </span>
        <textarea
          rows={5}
          value={form.description}
          onChange={(e) => onChange({ description: e.currentTarget.value })}
          className="w-full p-3 rounded-xl text-sm"
          style={{
            background: 'var(--bg-surface)',
            color: 'var(--fg-primary)',
            border: '1px solid var(--border-default)',
            font: 'inherit',
            resize: 'vertical',
          }}
        />
        <span
          className="block text-xs mt-1"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          Edit freely. Used as default across OTAs.
        </span>
      </label>
    </div>
  );
}

function Step6Confirm({
  form,
}: {
  readonly form: FormState;
}): React.ReactElement {
  return (
    <div>
      <h2
        className="m-0 mb-1 text-base font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Confirm
      </h2>
      <p
        className="m-0 mb-4 text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        Review and create. You can edit everything after.
      </p>
      <div
        className="rounded-xl p-4"
        style={{ background: 'var(--bg-sunken)' }}
      >
        <DetailRow label="Name" value={form.name || '—'} />
        <DetailRow label="Type" value={form.category} />
        <DetailRow
          label="Address"
          value={`${form.street}, ${form.city}, ${form.state} ${form.zip}`}
        />
        <DetailRow
          label="Composition"
          value={form.composition === 'whole' ? 'Whole property' : 'Per-space'}
        />
        <DetailRow
          label="Description"
          value={
            form.description.length > 0
              ? `${form.description.slice(0, 80)}${form.description.length > 80 ? '…' : ''}`
              : 'Pending'
          }
        />
        {/* Surface the hardcoded country + timezone defaults so the
            user sees them — v1 doesn't collect either via UI, and
            shipping them silently encodes an unmade product decision.
            When the wizard grows region/timezone pickers, these rows
            become editable (or move into Step 2 Address). */}
        <DetailRow
          label="Country"
          value={
            <span style={{ color: 'var(--fg-tertiary)' }}>
              US (default — region picker is a follow-up)
            </span>
          }
        />
        <DetailRow
          label="Timezone"
          value={
            <span style={{ color: 'var(--fg-tertiary)' }}>
              America/Los_Angeles (default)
            </span>
          }
        />
      </div>
    </div>
  );
}

function DiscardConfirm({
  onKeep,
  onDiscard,
}: {
  readonly onKeep: () => void;
  readonly onDiscard: () => void;
}): React.ReactElement {
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const keepRef = React.useRef<HTMLButtonElement>(null);
  // SCRUM-167 Codex blocker 2 fix: real focus trap with initial focus
  // on "Keep editing" (the non-destructive default), Tab containment,
  // and focus return to the opener (the wizard Cancel button) on close.
  useFocusTrap(dialogRef, { initialFocusRef: keepRef });
  // Esc closes (no commit) — same convention as the other property
  // dialogs.
  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onKeep();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onKeep]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'var(--bg-overlay)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onKeep();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="discard-confirm-title"
        className="w-full max-w-sm rounded-2xl p-6"
        style={{
          background: 'var(--bg-surface)',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        <h2
          id="discard-confirm-title"
          className="m-0 text-lg font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Discard this draft?
        </h2>
        <p
          className="mt-1 mb-4 text-sm"
          style={{ color: 'var(--fg-secondary)' }}
        >
          Anything you’ve entered will be lost.
        </p>
        <div className="flex justify-end gap-2">
          <Button ref={keepRef} variant="ghost" onClick={onKeep}>
            Keep editing
          </Button>
          <Button variant="danger" onClick={onDiscard}>
            Discard
          </Button>
        </div>
      </div>
    </div>
  );
}
